import "./polyfill/window.polyfill";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { Theme } from "@mui/material/styles";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { Provider } from "react-redux";
import { Auth0Context, Auth0Provider } from "@auth0/auth0-react";

import DynamicThemeProvider from "./providers/DynamicThemeProvider";

import "react-phone-input-2/lib/style.css";
import "flagpack/dist/flagpack.css";
import "react-image-crop/dist/ReactCrop.css";

import * as Sentry from "@sentry/react";
import mixpanelInit from "./lib/mixpanel";
import "./lib/signal";

import "./index.css";
import ErrorBoundary from "./components/atoms/ErrorBoundary";

import App from "./App";
import combineComponents from "./utilities/combineComponents";

import store from "./store";
import { clearAllDrafts } from "./lib/idb";
import { registerWorker } from "./setupWorker";

declare module "@mui/styles/defaultTheme" {
  // eslint-disable-next-line @typescript-eslint/no-empty-interface
  interface DefaultTheme extends Theme {}
}

if (import.meta.env.REACT_APP_BUG_TRACKING_URI) {
  Sentry.init({
    dsn: import.meta.env.REACT_APP_BUG_TRACKING_URI,
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0,
    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,
    integrations: [new Sentry.Replay()],
  });
}

mixpanelInit();

const wrappers = [
  ErrorBoundary,
  BrowserRouter,
  DynamicThemeProvider,
  (props) => <DndProvider backend={HTML5Backend} {...props} />,
  (props) => <LocalizationProvider dateAdapter={AdapterMoment} {...props} />,
  (props) => <Provider store={store} {...props} />,
];

const AppWrappers = combineComponents(...wrappers);

sessionStorage.setItem("loaded date", new Date().toISOString());

//@ts-ignore -- ios height fix
// var isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream
// if (isIOS) {
//   document.body.style.height = '100%'
// }
(async () => await clearAllDrafts())();

const container = document.getElementById("root");
const root = createRoot(container);

const deferred = (() => {
  const props = {};
  // @ts-ignore
  props.promise = new Promise((resolve) => (props.resolve = resolve));
  return props;
})();

export const getAccessToken = async () => {
  // @ts-ignore
  const getToken = await deferred.promise;
  return getToken();
};

registerWorker();

root.render(
  <Auth0Provider
    domain={import.meta.env.REACT_APP_AUTH0_DOMAIN}
    clientId={import.meta.env.REACT_APP_AUTH0_CLIENT_ID}
    authorizationParams={{
      redirect_uri: `${window.location.origin}/login`,
      audience: `https://${import.meta.env.REACT_APP_AUTH0_DOMAIN}/api/v2/`,
      scope: "openid email profile offline_access",
    }}
    useRefreshTokens={true}
    useRefreshTokensFallback={true}
    cacheLocation="localstorage"
  >
    <Auth0Context.Consumer>
      {({ getAccessTokenSilently }: any) => {
        // @ts-ignore
        deferred.resolve(getAccessTokenSilently);
        return (
          <AppWrappers>
            <App />
          </AppWrappers>
        );
      }}
    </Auth0Context.Consumer>
  </Auth0Provider>,
);
